// My Tasks Dashboard
// Execution personnel view of assigned tasks

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, AlertCircle, CheckCircle, Play, AlertTriangle, Activity, MapPin } from 'lucide-react';
import { useUserTasks } from '../../hooks/useTasks';
import { useTaskMutations } from '../../hooks/useTasks';
import { riskCalculationService } from '../../services/riskCalculationService';
import { departmentService, roomService } from '../../services/firebaseService';
import TaskExecutionView from './TaskExecutionView';
import RiskAlertModal from './RiskAlertModal';
import CompleteATPModal from './CompleteATPModal';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import { useOrg } from '../../hooks/useOrg';
import './MyTasksDashboard.css';

const MyTasksDashboard = ({ userId }) => {
  const { t } = useTranslation(['tasks', 'common', 'ipc']);
  const { notify } = useNotifications();
  const { activeOrgId } = useOrg();
  const [statusFilter, setStatusFilter] = useState('pending'); // pending | overdue | in_progress | completed
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'dueDate', 'risk', 'priority'
  const [selectedTask, setSelectedTask] = useState(null);
  const [executingTask, setExecutingTask] = useState(null);
  const [showRiskAlert, setShowRiskAlert] = useState(null);
  
  // Location filter and group-by (Phase 1)
  const [locationDepartmentId, setLocationDepartmentId] = useState('');
  const [locationRoomId, setLocationRoomId] = useState('');
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'department' | 'room'
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  
  // Phase 3: Replace browser dialogs with modals
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [atpModalTask, setAtpModalTask] = useState(null);

  const locationFilters = {};
  if (locationDepartmentId) locationFilters.departmentId = locationDepartmentId;
  if (locationRoomId) locationFilters.roomId = locationRoomId;
  const { tasks, loading, error, refresh } = useUserTasks(userId, locationFilters);

  useEffect(() => {
    const loadClinical = async () => {
      try {
        const [depts, rms] = await Promise.all([
          departmentService.getAll(),
          roomService.getAll()
        ]);
        setDepartments(depts || []);
        setRooms(rms || []);
      } catch (e) {
        console.error('My Tasks: failed to load departments/rooms', e);
      }
    };
    loadClinical();
  }, []);
  const { startTask, acknowledgeTask } = useTaskMutations();

  // Get risk level from RPN
  const getRiskLevel = (rpn) => {
    if (!rpn) return null;
    return riskCalculationService.getRiskLevel(rpn);
  };

  // Rooms available for current department filter
  const roomsInDepartment = locationDepartmentId
    ? rooms.filter(r => r.departmentId === locationDepartmentId)
    : rooms;

  const isOverdue = (task) => {
    if (task.status === 'completed' || task.status === 'cancelled') return false;
    const now = Date.now();
    const scheduledEnd = task.scheduledEnd?.toMillis ? task.scheduledEnd.toMillis() : (task.scheduledEnd ? new Date(task.scheduledEnd).getTime() : 0);
    return scheduledEnd > 0 && now > scheduledEnd;
  };

  // Filter tasks by status: Pending (assigned + acknowledged), Overdue, In progress, Completed. No "All tasks".
  const filteredTasks = tasks.filter(task => {
    if (statusFilter === 'pending') {
      return (
        task.status === 'assigned' ||
        task.status === 'acknowledged' ||
        task.status === 'pending_assignment' ||
        task.status === 'pending'
      );
    }
    if (statusFilter === 'overdue') return isOverdue(task); // assigned, acknowledged, or in_progress and past due
    if (statusFilter === 'in_progress') return task.status === 'in_progress';
    if (statusFilter === 'completed') return task.status === 'completed';
    return false;
  }).sort((a, b) => {
    if (sortBy === 'newest') {
      const aCreated = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime?.() ?? 0);
      const bCreated = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime?.() ?? 0);
      if (bCreated !== aCreated) return bCreated - aCreated;
      const aDue = a.scheduledEnd?.toMillis ? a.scheduledEnd.toMillis() : (a.scheduledEnd ? new Date(a.scheduledEnd).getTime() : 0);
      const bDue = b.scheduledEnd?.toMillis ? b.scheduledEnd.toMillis() : (b.scheduledEnd ? new Date(b.scheduledEnd).getTime() : 0);
      return bDue - aDue;
    }
    if (sortBy === 'risk') {
      const aRPN = a.risk_data?.max_rpn || 0;
      const bRPN = b.risk_data?.max_rpn || 0;
      return bRPN - aRPN; // Highest risk first
    }
    if (sortBy === 'priority') {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      return bPriority - aPriority;
    }
    // Due date: earliest due first (overdue at top)
    const aDue = a.scheduledEnd?.toMillis ? a.scheduledEnd.toMillis() : (a.scheduledEnd ? new Date(a.scheduledEnd).getTime() : 0);
    const bDue = b.scheduledEnd?.toMillis ? b.scheduledEnd.toMillis() : (b.scheduledEnd ? new Date(b.scheduledEnd).getTime() : 0);
    return aDue - bDue;
  });

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t('tasks:notSet');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Format "Protocol X – Step Y – Action Z" from task's first protocol (generator level)
  const getTaskProtocolStepActionLabel = (task) => {
    const p = task.protocols?.[0];
    if (!p) return task.name || '';
    const parts = [p.protocolName || t('tasks:protocol')];
    if (p.generatorLevel === 'action' && (p.generatorStepName || p.generatorActionName)) {
      if (p.generatorStepName) parts.push(p.generatorStepName);
      if (p.generatorActionName) parts.push(p.generatorActionName);
    } else if (p.generatorLevel === 'step' && p.generatorStepName) {
      parts.push(p.generatorStepName);
    }
    return parts.join(' – ');
  };

  /** Where the task is executed: one location per task (department → room or department only). */
  const getTaskExecuteInLabel = (task) => {
    const ctx = task.context || {};
    if (ctx.roomName || ctx.departmentName) {
      return [ctx.departmentName, ctx.roomName].filter(Boolean).join(' → ');
    }
    return t('tasks:notSpecified');
  };

  const getTaskProgress = (task) => {
    let totalSteps = 0;
    let completedSteps = 0;
    
    task.protocols.forEach(protocol => {
      totalSteps += protocol.steps.length;
      completedSteps += protocol.steps.filter(s => s.isComplete || s.isSkipped).length;
    });
    
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  // Group tasks by location (each task has at most one room/department)
  const getGroupKey = (task, by) => {
    const ctx = task.context || {};
    if (by === 'department') return ctx.departmentId || ctx.departmentName || 'unspecified';
    if (by === 'room') return ctx.roomId || ctx.roomName || 'unspecified';
    return null;
  };
  const getGroupLabel = (key, by) => {
    if (key === 'unspecified') return t('tasks:unspecifiedLocation');
    if (by === 'department') return departments.find(d => d.id === key || d.name === key)?.name || key;
    if (by === 'room') return rooms.find(r => r.id === key || r.name === key)?.name || key;
    return key;
  };
  const groupedTasks = groupBy !== 'none'
    ? filteredTasks.reduce((acc, task) => {
        const key = getGroupKey(task, groupBy);
        if (!acc[key]) acc[key] = [];
        acc[key].push(task);
        return acc;
      }, {})
    : null;
  const groupKeys = groupedTasks ? Object.keys(groupedTasks).sort((a, b) => {
    if (a === 'unspecified') return 1;
    if (b === 'unspecified') return -1;
    return getGroupLabel(a, groupBy).localeCompare(getGroupLabel(b, groupBy));
  }) : [];

  const handleStartTask = async (task) => {
    // Show risk alert if task has critical risks
    if (task.risk_data?.max_rpn >= 500) {
      setShowRiskAlert(task);
      return;
    }
    
    try {
      await startTask(task.id, userId);
      setExecutingTask(task);
      refresh();
    } catch (error) {
      setErrorModal({ isOpen: true, message: `${t('tasks:errorStartingTask')} ${error.message}` });
    }
  };

  const handleConfirmHighRiskTask = async (task) => {
    setShowRiskAlert(null);
    try {
      await startTask(task.id, userId);
      setExecutingTask(task);
      refresh();
    } catch (error) {
      setErrorModal({ isOpen: true, message: `${t('tasks:errorStartingTask')} ${error.message}` });
    }
  };

  const handleAcknowledge = async (task) => {
    try {
      await acknowledgeTask(task.id, userId);
      refresh();
    } catch (error) {
      setErrorModal({ isOpen: true, message: `Error acknowledging task: ${error.message}` });
    }
  };

  const handleViewTask = (task) => {
    setExecutingTask(task);
  };

  const handleBackToList = () => {
    setExecutingTask(null);
    refresh();
  };

  const openAtpModal = (task) => {
    const ctx = task.atp_context || {};
    if (!ctx.room_name || !ctx.surface_type_name) {
      notify.error(t('ipc:atp.incompleteTaskData'));
      return;
    }
    if (task.organization_id && activeOrgId && task.organization_id !== activeOrgId) {
      notify.error(t('ipc:atp.organizationMismatch'));
      return;
    }
    setAtpModalTask(task);
  };

  const renderTaskCard = (task) => {
    const overdue = isOverdue(task);
    const progress = getTaskProgress(task);
    return (
      <div key={task.id} className={`task-card ${overdue ? 'overdue' : ''}`}>
        {overdue && (
          <div className="overdue-badge">
            <AlertCircle size={14} /> {t('tasks:overdueBadge')}
          </div>
        )}
        <div className="task-card-header">
          <div>
            <h3>{task.name}</h3>
            <p className="task-protocol-step-action">{getTaskProtocolStepActionLabel(task)}</p>
          </div>
          <div className="badges-group">
            {task.risk_data?.max_rpn >= 500 && (
              <span className="risk-badge critical" title={t('tasks:criticalRisk')}>
                <AlertTriangle size={12} />
                {t('tasks:criticalRisk')}
              </span>
            )}
            {task.risk_data?.max_rpn >= 200 && task.risk_data?.max_rpn < 500 && (
              <span className="risk-badge high" title={t('tasks:highRisk')}>
                <AlertCircle size={12} />
                {t('tasks:highRisk')}
              </span>
            )}
            <span className={`priority-badge priority-${task.priority}`}>
              {task.priority}
            </span>
          </div>
        </div>
        <div className="task-details">
          <div className="detail-row">
            <Clock size={14} />
            <span>{t('tasks:starting')}: {formatDateTime(task.scheduledStart)} · {t('tasks:due')}: {formatDateTime(task.scheduledEnd)}</span>
          </div>
          <div className="detail-row assignment-info">
            <span>
              {t('tasks:assignedTo')}: {task.assignedToGroup
                ? (task.assignedToGroupName || t('tasks:group'))
                : (task.assignedToName || t('tasks:unassigned'))}
              {task.assignedToGroup && !task.assignedTo && (
                <span className="group-claim-hint"> {t('tasks:anyMemberCanClaim')}</span>
              )}
            </span>
          </div>
          <div className="detail-row execute-in">
            <span>{t('tasks:executeIn')}: <strong>{getTaskExecuteInLabel(task)}</strong></span>
          </div>
          {task.risk_data && (
            <div className="detail-row risk-info">
              <Activity size={14} />
              <span>
                Max RPN: <strong>{task.risk_data.max_rpn}</strong>
                {task.risk_data.risk_count > 0 && (
                  <span className="risk-count"> ({task.risk_data.risk_count} risks)</span>
                )}
              </span>
            </div>
          )}
          {task.risk_data?.biological_entities && task.risk_data.biological_entities.length > 0 && (
            <div className="detail-row biological-entities">
              <span className="entity-label">{t('tasks:biologicalEntities')}</span>
              <div className="entities-list">
                {task.risk_data.biological_entities.slice(0, 3).map((entity, idx) => (
                  <span key={entity.id || idx} className="entity-badge">
                    {entity.name}
                  </span>
                ))}
                {task.risk_data.biological_entities.length > 3 && (
                  <span className="entity-more">+{task.risk_data.biological_entities.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>
        {task.status === 'in_progress' && (
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            <span className="progress-text">{t('tasks:percentComplete', { percent: progress })}</span>
          </div>
        )}
        <div className="task-actions">
          {task.type === 'atp_check' && task.status === 'pending' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => openAtpModal(task)}
            >
              {t('tasks:completeATPCheck')}
            </button>
          )}
          {task.status === 'assigned' && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => handleAcknowledge(task)}
              >
                {t('tasks:acknowledge')}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleStartTask(task)}
              >
                <Play size={16} /> {t('tasks:startTask')}
              </button>
            </>
          )}
          {task.status === 'acknowledged' && (
            <button
              className="btn btn-primary"
              onClick={() => handleStartTask(task)}
            >
              <Play size={16} /> {t('tasks:startTask')}
            </button>
          )}
          {task.status === 'in_progress' && (
            <button
              className="btn btn-primary"
              onClick={() => handleViewTask(task)}
            >
              {t('tasks:continueTask')}
            </button>
          )}
          {task.status === 'completed' && (
            <button
              className="btn btn-secondary"
              onClick={() => handleViewTask(task)}
            >
              View Details
            </button>
          )}
        </div>
      </div>
    );
  };

  // If executing a task, show execution view
  if (executingTask) {
    return (
      <TaskExecutionView
        task={executingTask}
        userId={userId}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="my-tasks-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>{t('tasks:myTasks')}</h2>
          <p className="subtitle">{t('tasks:subtitle')}</p>
        </div>
      </div>

      {/* Task groups: Pending (assigned + acknowledged), Overdue, In progress, Completed */}
      <div className="filters-section">
        <div className="status-filter">
          <button
            className={`btn btn-secondary ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            {t('tasks:pending')}
          </button>
          <button
            className={`btn btn-secondary ${statusFilter === 'overdue' ? 'active' : ''}`}
            onClick={() => setStatusFilter('overdue')}
          >
            {t('tasks:overdue')}
          </button>
          <button
            className={`btn btn-secondary ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
          >
            {t('tasks:inProgress')}
          </button>
          <button
            className={`btn btn-secondary ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            {t('tasks:completed')}
          </button>
        </div>
        <div className="sort-filter">
          <label>{t('tasks:sortBy')}</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="newest">{t('tasks:newestFirst')}</option>
            <option value="dueDate">{t('tasks:dueDate')}</option>
            <option value="risk">{t('tasks:riskLevel')}</option>
            <option value="priority">{t('tasks:priority')}</option>
          </select>
        </div>
      </div>

      {/* Location filter and group-by (Phase 1) */}
      <div className="location-filters">
        <div className="location-filter-row">
          <MapPin size={16} className="location-filter-icon" />
          <label className="location-label">{t('tasks:department')}</label>
          <select
            className="location-select"
            value={locationDepartmentId}
            onChange={(e) => {
              setLocationDepartmentId(e.target.value);
              setLocationRoomId('');
            }}
          >
            <option value="">{t('tasks:allDepartments')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <label className="location-label">{t('tasks:room')}</label>
          <select
            className="location-select"
            value={locationRoomId}
            onChange={(e) => setLocationRoomId(e.target.value)}
          >
            <option value="">{t('tasks:allRooms')}</option>
            {roomsInDepartment.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <label className="location-label">{t('tasks:groupBy')}</label>
          <select
            className="location-select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="none">{t('tasks:none')}</option>
            <option value="department">{t('tasks:department')}</option>
            <option value="room">{t('tasks:roomLabel')}</option>
          </select>
        </div>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('tasks:loadingTasks')}</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{t('tasks:errorLoadingTasks')} {error}</p>
          <button onClick={refresh} className="btn btn-secondary">{t('tasks:retry')}</button>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} color="#10b981" />
          <p>{t('tasks:noTasks', { status: t('tasks:' + (statusFilter === 'in_progress' ? 'inProgress' : statusFilter)) })}</p>
          <p className="empty-hint">{t('tasks:allCaughtUp')}</p>
          <p className="empty-tip">
            {t('tasks:emptyHint')}
          </p>
        </div>
      ) : (
        <div className="tasks-list-container">
          {groupBy === 'none' ? (
            <div className="tasks-list">
              {filteredTasks.map(task => renderTaskCard(task))}
            </div>
          ) : (
            groupKeys.map(key => (
              <div key={key} className="task-group">
                <h4 className="task-group-title">{getGroupLabel(key, groupBy)}</h4>
                <div className="tasks-list">
                  {groupedTasks[key].map(task => renderTaskCard(task))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Risk Alert Modal */}
      {showRiskAlert && (
        <RiskAlertModal
          task={showRiskAlert}
          onConfirm={() => handleConfirmHighRiskTask(showRiskAlert)}
          onCancel={() => setShowRiskAlert(null)}
        />
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title={t('common:error')}
        size="small"
      >
        <div className="error-message-content">
          <p>{errorModal.message}</p>
          <button className="btn btn-primary" onClick={() => setErrorModal({ isOpen: false, message: '' })}>
            {t('tasks:close')}
          </button>
        </div>
      </Modal>

      <CompleteATPModal
        isOpen={!!atpModalTask}
        onClose={() => setAtpModalTask(null)}
        task={atpModalTask}
        userId={userId}
        onCompleted={refresh}
      />
    </div>
  );
};

export default MyTasksDashboard;


